/**
 * Agendamento dos efeitos do Preact com a aba oculta (§15.6, AC-15.6-07; §3.6.4 regra 2): o `useEffect` roda depois do
 * próximo quadro, por `requestAnimationFrame` com um `setTimeout` de 35 ms em paralelo (preact/hooks, `afterNextFrame`).
 * Com a aba oculta, um snapshot que chega (ou o próprio aviso de aba oculta) re-renderiza e pediria um rAF; aqui, oculta,
 * o efeito sai só pelo `setTimeout` (o mesmo caminho que o Preact já usa quando o rAF não dispara) e nenhum rAF é
 * pedido. Visível, o agendamento é o padrão do Preact.
 */
import { options } from 'preact';

/** Prazo do `setTimeout` paralelo ao rAF (o `RAF_TIMEOUT` do preact/hooks). */
export const EFFECT_RAF_TIMEOUT_MS = 35;

/** Agenda `cb` depois do próximo quadro (visível) ou na próxima volta do laço de eventos (oculta). */
export function scheduleAfterPaint(cb: () => void): void {
  if (typeof document !== 'undefined' && document.hidden) {
    setTimeout(cb);
    return;
  }
  let raf = 0;
  const done = () => {
    clearTimeout(timer);
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    setTimeout(cb);
  };
  const timer = setTimeout(done, EFFECT_RAF_TIMEOUT_MS);
  if (typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(done);
}

/** Liga o agendamento em `options.requestAnimationFrame`; devolve a função que restaura o anterior. */
export function installHiddenEffects(): () => void {
  const prev = options.requestAnimationFrame;
  options.requestAnimationFrame = scheduleAfterPaint;
  return () => {
    if (options.requestAnimationFrame === scheduleAfterPaint) options.requestAnimationFrame = prev;
  };
}
